if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';

      // Plan purchase build (docs/plans/imei-province-e911-plan.md Section 4):
      // present only on the plan template's PDP. When set, add-to-cart pairs
      // the plan line with its province's E911 fee line atomically.
      //
      // NOTE: must use getAttribute('id'), not this.form.id — this form
      // has a field named "id" (the hidden variant-id input, standard
      // Shopify pattern), and per the HTML spec a named form control
      // shadows the form element's own .id property, so this.form.id
      // silently returns that <input> element instead of the id string.
      this.deviceGate = document.querySelector(`product-device-gate[data-form-id="${this.form.getAttribute('id')}"]`);
    }

    // One plan per order (spec: "adding a different plan REPLACES the
    // existing plan line and swaps the E911 line"). Zeroes out any existing
    // plan+E911 pair via sequential /cart/change.js calls before the new
    // pair is added — highest line index first so removing one doesn't
    // shift the other's index out from under it. A parallel, smaller
    // version of the cascade-removal cart.js's CartItems.updateQuantity
    // does for a manual plan-line removal; kept separate since this call
    // site has no CartItems instance to reuse.
    async removeExistingPlanPair() {
      const planEl = document.querySelector('[data-line-type="plan"]');
      if (!planEl) return;

      const e911El = document.querySelector('[data-line-type="e911"]');
      const planIndex = planEl.querySelector('[data-index]')?.dataset.index;
      const e911Index = e911El ? e911El.querySelector('[data-index]')?.dataset.index : null;

      const lines = [planIndex, e911Index].filter(Boolean).map(Number).sort((a, b) => b - a);
      for (const line of lines) {
        await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({ line, quantity: 0 })
        });
      }
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.customerConfirmationPending) return;

      if (this.deviceGate) {
        if (!window.SpeakoutCustomerConfirmation) return;
        this.customerConfirmationPending = true;
        const confirmed = await window.SpeakoutCustomerConfirmation.confirm(this.submitButton);
        this.customerConfirmationPending = false;
        if (!confirmed || !this.isConnected || !this.deviceGate.isGateValid() || this.submitButton.getAttribute('aria-disabled') === 'true') return;
      }

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      const sectionsToRender = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : null;

      if (this.deviceGate) {
        await this.removeExistingPlanPair();

        // A single multipart FormData post can only ever describe one cart
        // line, so the plan+E911 pair goes through the JSON items[] form of
        // /cart/add.js instead — one atomic request, both lines or neither.
        const planVariantId = formData.get('id');
        const provinceSelect = this.deviceGate.querySelector('select[name="properties[province]"]');
        const provinceOption = provinceSelect ? provinceSelect.selectedOptions[0] : null;
        const e911VariantId = provinceOption ? provinceOption.dataset.e911VariantId : null;

        const items = [{
          id: planVariantId,
          quantity: 1,
          properties: {
            [LINE_PROPERTY_KEYS.imei]: formData.get(`properties[${LINE_PROPERTY_KEYS.imei}]`),
            [LINE_PROPERTY_KEYS.province]: formData.get(`properties[${LINE_PROPERTY_KEYS.province}]`)
          }
        }];

        if (e911VariantId) {
          items.push({
            id: e911VariantId,
            quantity: 1,
            properties: { [LINE_PROPERTY_KEYS.planLink]: planVariantId }
          });
        }

        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify({
          items,
          sections: sectionsToRender,
          sections_url: window.location.pathname
        });
      } else {
        if (this.cart) {
          formData.append('sections', sectionsToRender);
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;
      }

      if (this.cart) this.cart.setActiveElement(document.activeElement);

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
